import { Permission, StateTransitionPermission } from '@idc-forms/permissions'
import { z } from 'zod'
import { optionalString, requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Rol de la plataforma (permisos + permisos por estado + usuarios asignados) —
 * fuente única de verdad para la validación del API (nestjs-zod
 * `createZodDto`) y del editor web (página completa con PermissionMatrix).
 *
 * `name` es único GLOBAL (la UI y el deep-link identifican al rol por nombre).
 * `permissions` se valida contra el catálogo agregado de @idc-forms/permissions
 * (el legacy hacía IsEnum(Permission)); `users` es la relación INVERSA
 * (user.roles) que el service escribe aparte — no vive en el documento del rol.
 */

// ObjectId de Mongo serializado (24 hex): el wire actual referencia estados y
// usuarios por ObjectId; no cambiar hasta contrato v2.
const objectIdString = z
  .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
  .regex(/^[a-f0-9]{24}$/i, 'Identificador inválido')

/** Permisos de transición sobre un estado concreto del flujo. */
export const StatePermissionInputSchema = z.object({
  stateId: objectIdString,
  permissions: z.array(z.nativeEnum(StateTransitionPermission)).default([])
})

const RoleInputBaseSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  description: optionalString(),
  permissions: z.array(z.nativeEnum(Permission)).optional(),
  statePermissions: z.array(StatePermissionInputSchema).optional(),
  /** Usuarios que TIENEN el rol (relación inversa; el service la reconcilia). */
  users: z.array(objectIdString).optional(),
  active: z.boolean().optional()
})

export const CreateRoleInputSchema = RoleInputBaseSchema

export const UpdateRoleInputSchema = RoleInputBaseSchema.partial()

export type StatePermissionInput = z.infer<typeof StatePermissionInputSchema>
export type CreateRoleInput = z.infer<typeof CreateRoleInputSchema>
export type UpdateRoleInput = z.infer<typeof UpdateRoleInputSchema>
